// @ts-check
import PlaceItemModel from './place-item.model';
import PlaceSingleItemModel from './place-single-item.model';
import { BETSLIP_TYPES, BETSLIP_LAYOUTS, BETSLIP_GAME_MODES, PLATFORMS, BETSLIP_ACCEPT_ODD_TYPE, ITEM_TYPES } from './const';
import { getLayout, getSelectedTab } from './index';
import { configStore } from '@bto-services/betslip-core/internals/config.store';

/**
 * @typedef {import('./interfaces').BetslipRaceOdd} BetslipRaceOdd
 * @typedef {import('./betbuilder/betbuilder-place.model').default} BetBuilderPlaceModel
 * @typedef {import('./interfaces').BetslipStore} BetslipStore
 * @typedef {import('./interfaces').BookedBetslip} BookedBetslip
 */

/**
 * Get the active build platform based on env 
 */
function getPlatform() {
	if (process.env.VUE_APP_IS_AGENT) {
		return PLATFORMS.ONLINE_SHOP;
	}
	if (process.env.VUE_APP_IS_MOBILE) {
		return PLATFORMS.MOBILE;
	}
	return PLATFORMS.WEB;
}

/**
 * Get bet item place model
 * @param {BetslipStore} data - betslip store
 * @param {string} itemKey - bet item key in store
 * @returns 
 */
const getPlaceItemModel = (data, itemKey) => {
	let item = data.items[itemKey];
	const match = data.matchItems[item.matchId];
	const banker = data.bankers[itemKey];
	const showBanker = data.showBankers &&
		(getSelectedTab(data) === BETSLIP_TYPES.SYSTEM || getLayout() === BETSLIP_LAYOUTS.EUROPEAN);
	const hasBanker = showBanker && banker;
	if (item.algo && data.algoItems[match.matchId] && data.algoItems[match.matchId].isActive && data.algoItems[match.matchId].singles.length === 1) {
		item = data.algoItems[match.matchId].singles[0];
	}
	if (item.algo && data.algoItems[match.matchId] && data.algoItems[match.matchId].isActive && data.algoItems[match.matchId].singles.length > 1) {
		item = { ...item, itemType: ITEM_TYPES.ALGO };
	}
	return new PlaceItemModel(item, match, hasBanker);
};

/**
 * Get racing each way odd
 * @param {PlaceItemModel} placeItemModel - original item
 * @param {BetslipRaceOdd} raceOdd - race odd for item
 * @returns {PlaceItemModel}
 */
const getRacePlaceModel = (placeItemModel, raceOdd) => ({
	...placeItemModel,
	oid: raceOdd.id,
	otoid: raceOdd.id,
	otid: raceOdd.marketId,
	oo: raceOdd.odd,
});

/**
 * Generate random string of size len
 * @param {number} len - random string size
 */
function generateRandomString(len) {
	let symbols = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
	let out = '';
	for (let i = len; i > 0; i -= 1) {
		out += symbols[Math.round(Math.random() * (symbols.length - 1))];
	}
	return out;
}

/**
 * Get folds depending on selected tab and layout
 * @param {BetslipStore} data - betslip store
 */
function getFolds(data) {
	const selectedTab = getSelectedTab(data);
	if (selectedTab === BETSLIP_TYPES.SINGLE) {
		return undefined;
	}
	if (selectedTab === BETSLIP_TYPES.COMBI) {
		const matchNum = Object.keys(data.matchItems).length;
		return { [matchNum]: data.folds[matchNum] };
	}
	// For system tab and European return all folds
	let folds = { ...data.folds };
	if (folds['-1']) {
		delete folds['-1'];
	}
	return folds;
}

/**
 * Checks if the betslip is booked one, and it there are any changes made
 * @param {BetslipStore} data
 * @returns {string|null}
*/
function bookedBetslipCheck(data) {
	if (!data.bookedBetslipDetails.bookCode) {
		return null;
	}
	const hasBookedEvents = data.bookedBetslipDetails.matchItems.some(key => data.matchItems[key]);
	if (hasBookedEvents) {
		return data.bookedBetslipDetails.bookCode;
	}
	return null;
}

export default class PlaceModel {
	/**
	 * 
	 * @param {BetslipStore} data - betslip Store
	 * @param {string} [assistedBetUserId] - assisted bet user password
	 */
	constructor(data, assistedBetUserId) {
		/**
		 * List of bet items
		 * @type {PlaceItemModel[]}
		 */
		this.absi = Object.keys(data.items).reduce((acc, itemKey) => {
			const placeItemModel = getPlaceItemModel(data, itemKey);
			acc.push(placeItemModel);
			if (data.raceOdds[itemKey]) {
				const raceOdd = data.items[itemKey].raceOdd;
				acc.push(getRacePlaceModel(placeItemModel, raceOdd));
			}

			return acc;
		}, []);

		/**
		 * List of single items,  
		 * only filled if tab is SINGLE or EU
		 * @type {PlaceSingleItemModel[]}
		 */
		this.bs = [];
		const itemsCount = Object.keys(data.items).length;
		// Only fill bs for single tab or for 
		if (getSelectedTab(data) === BETSLIP_TYPES.SINGLE || getLayout(itemsCount, data.freebetId) === BETSLIP_LAYOUTS.EUROPEAN) {
			this.bs = Object.keys(data.singles).reduce((acc, itemKey) => {
				const singleStake = +data.singles[itemKey];
				// For EUROPEAN LAYOUT if single stake is not set don't add it in bs
				if (getLayout() === BETSLIP_LAYOUTS.EUROPEAN && !singleStake) {
					return acc;
				}
				const placeItemModel = getPlaceItemModel(data, itemKey);

				const singleItemModel = new PlaceSingleItemModel(placeItemModel, singleStake);
				acc.push(singleItemModel);
				if (data.raceOdds[itemKey]) {
					const raceOdd = data.items[itemKey].raceOdd;
					const raceItemModel = getRacePlaceModel(placeItemModel, raceOdd);
					const raceSingleItemModel = new PlaceSingleItemModel(raceItemModel, singleStake);
					acc.push(raceSingleItemModel);
				}
				return acc;
			}, []);
		}
		/**
		 * Freebet id if selected
		 * @type {number}
		 */
		this.fbid = data.freebetId || null;
		/**
		 * Betslip type enum
		 * @type {number}
		 */
		this.bstid = this.fbid ? BETSLIP_GAME_MODES.FREEBET : getSelectedTab(data);
		/**
		 * Total betslip match items
		 * @type {number}
		 */
		this.ti = Object.keys(data.matchItems).length;
		/**
		 * Total bankers in bet
		 * @type {number}
		 */
		// this.tb = Object.keys(data.bankers).length;
		this.tb = 0;
		/**
		 * Total system combination items
		 * @type {number}
		 */
		this.sci = 0;
		/**
		 * Special combination id
		 * @type {number}
		 */
		this.scid = 0;
		/**
		 * Platform id (WEB,MOBILE,AGENT)
		 * @type {ReturnType<getPlatform>}
		 */
		this.p = getPlatform();
		/**
		 * Bet mode
		 * // TODO: check this
		 * @type {number}
		 */
		this.bm = 0;
		/**
		 * Flag if this is Agent app
		 * @type {boolean}
		 */
		this.ios = !!process.env.VUE_APP_IS_AGENT;
		/**
		 * Max odds
		 * @type {string}
		 */
		this.mao = data.totals.maxOdds || '0';
		/**
		 * Min odds
		 * @type {string}
		 */
		this.mio = data.totals.minOdds || '0';
		/**
		 * Min winnings
		 * @type {number}
		 */
		// this.miw = data.bonuses.minWin || 0;
		this.miw = 0;
		/**
		 * Max winnings
		 * @type {number}
		 */
		// this.maw = data.bonuses.maxWin || 0;
		this.maw = 0;
		/**
		 * Betslip identifier
		 * @type {string}
		 */
		this.id = generateRandomString(10);
		/**
		 * Map of folds -> stake amount for fold
		 * @type {{[x:number]:string}}
		 */
		this.f = getFolds(data);
		/**
		 * Total stake
		 * @type {number}
		 */
		this.am = (data.totals && data.totals.stake) || 0;
		/**
		 * Device guid
		 * @type {string}
		 */
		this.dguid = '';
		/**
		 * Depracated starint 5.6 version
		 * Accept odds change flag
		 * @type {boolean}
		 */
		// this.aoc = data.acceptOddChange;
		/**
		 * Accept odds change enum
		 * @type {import('bto-models/betslip/interfaces').BETSLIP_ACCEPT_ODD_TYPE_ENUMS}
		 */
		this.aoce = data.acceptOddChange;
		// TODO: remove after all clients have been deployed 5.6 version
		if (typeof data.acceptOddChange === 'boolean') {
			this.aoce = data.acceptOddChange ? BETSLIP_ACCEPT_ODD_TYPE.ALL : BETSLIP_ACCEPT_ODD_TYPE.NO;
		}
		/**
		 * Assisted bet user id
		 * used for assited betting on agent
		 * currently it's the password from client search
		 * @type {string}
		 */
		this.uid = assistedBetUserId || null;

		// If manual auth is selected add manual auth and note in place model
		if (data.manualAuth) {
			/**
			 * Manual auth flag
			 * @type {boolean|undefined}
			 */
			this.ma = data.manualAuth;
			/**
			 * Note for why ticket was sent to manual auth
			 * @type {string|undefined}
			 */
			this.n = data.note;
		}
		/**
		 * Flag if assisted user is present
		 */
		this.refu = !!assistedBetUserId;
		/**
		 * Flag wheather to take funds from affilate(assisted bet user)
		 * @type {boolean}
		 */
		this.tfa = !!assistedBetUserId;
		/**
		 * Bonus brand id
		 * // TODO: when we recieve bonus brand from WP/CDN pass it here
		 */
		this.bbid = 0;
		/**
		 * Bet builder selections additional properites
		 * @type {BetBuilderPlaceModel[]}
		 */
		this.bb = [];
		if(configStore.settings.ALGO_ENABLED){
			/**
			 * Algo BetBuilder selections additional properites
			 */
			this.abb = [];
		}
		/**
		 * Booking code
		 * @type {string|null}
		*/
		this.bc = bookedBetslipCheck(data);
	}
}